import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useModal } from '~/contexts';
import settings from '~/settings';

/**
 * Headless selection manager for row IDs.
 * rows: array of { id }
 * visibleRows: optional rows in display order for Shift+click ranges (defaults to rows)
 * limit: optional cap on selected IDs
 * onChange: optional callback(ids[])
 */
export default function useRowSelection({ rows, visibleRows, limit = settings.bulkActionItemLimit, onChange }) {
  const allIds = useMemo(() => (rows ?? []).map((r) => r.id), [rows]);
  const visibleIds = useMemo(() => (visibleRows ? visibleRows.map((r) => r.id) : allIds), [visibleRows, allIds]);
  const [ids, setIds] = useState([]);
  const anchorRef = useRef(null);
  const { hasInstances: hasModalInstances } = useModal();

  // Ensure selection stays valid when rows/limit change
  useEffect(() => {
    setIds((prev) => prev.filter((id) => allIds.includes(id)).slice(0, limit));
  }, [allIds, limit]);

  // Centralized setter that enforces uniqueness, membership, and limit
  const set = useCallback(
    (next) => {
      setIds((prev) => {
        const raw = typeof next === 'function' ? next(prev) : next;
        const unique = Array.from(new Set(raw))
          .filter((id) => allIds.includes(id))
          .slice(0, limit);
        onChange?.(unique);
        return unique;
      });
    },
    [allIds, limit, onChange],
  );

  const clear = useCallback(() => {
    anchorRef.current = null;
    set([]);
  }, [set]);

  const select = useCallback(
    (id) => set((curr) => (curr.includes(id) || curr.length >= limit ? curr : [...curr, id])),
    [set, limit],
  );

  const deselect = useCallback((id) => set((curr) => curr.filter((x) => x !== id)), [set]);

  const toggle = useCallback(
    (id, { range = false } = {}) => {
      const from = visibleIds.indexOf(anchorRef.current);
      const to = visibleIds.indexOf(id);
      anchorRef.current = id;

      // Shift+click applies the clicked row's new state to every row from the anchor to it.
      if (range && from !== -1 && to !== -1) {
        const rangeIds = visibleIds.slice(Math.min(from, to), Math.max(from, to) + 1);
        // Anchor first, so the item limit trims the far end of the range.
        if (from > to) rangeIds.reverse();
        set((curr) => (curr.includes(id) ? curr.filter((x) => !rangeIds.includes(x)) : [...curr, ...rangeIds]));
        return;
      }

      set((curr) => (curr.includes(id) ? curr.filter((x) => x !== id) : curr.length >= limit ? curr : [...curr, id]));
    },
    [set, limit, visibleIds],
  );

  const selectMany = useCallback(
    (addIds) =>
      set((curr) => {
        const next = [...curr];
        for (const id of addIds) {
          if (next.length >= limit) break;
          if (allIds.includes(id) && !next.includes(id)) next.push(id);
        }
        return next;
      }),
    [set, limit, allIds],
  );

  const selectAll = useCallback(() => set(allIds.slice(0, limit)), [set, allIds, limit]);

  const toggleAll = useCallback(() => {
    anchorRef.current = null;
    set((curr) => (curr.length > 0 ? [] : allIds.slice(0, limit)));
  }, [set, allIds, limit]);

  const isSelected = useCallback((id) => ids.includes(id), [ids]);

  const count = ids.length;
  const total = allIds.length;
  const any = count > 0;
  const all = total > 0 && count === Math.min(total, limit);
  const some = any && !all;
  const limitReached = count >= limit;

  const isDisabled = useCallback((id) => !ids.includes(id) && limitReached, [ids, limitReached]);

  // Esc clears only when nothing else can own it: focus on the page or a checkbox, and no drawer, modal, or menu open.
  useEffect(() => {
    if (!any) return;

    const handleKeyDown = (e) => {
      if (e.key !== 'Escape' || e.defaultPrevented || hasModalInstances() || document.querySelector('.is-open')) return;
      const isCheckbox = e.target instanceof HTMLInputElement && e.target.type === 'checkbox';
      if (e.target !== document.body && !isCheckbox) return;
      clear();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [any, clear, hasModalInstances]);

  return {
    // state
    ids,
    count,
    total,
    limit,

    // derived flags
    any,
    all,
    some,
    limitReached,

    // queries
    isSelected,
    isDisabled,

    // mutations
    select,
    deselect,
    toggle,
    selectMany,
    selectAll,
    toggleAll,
    clear,
  };
}
